import clsx from 'clsx';
import LegacySemanticUiIcon, {
  IconName,
} from 'components/common/LegacySemanticUiIcon/LegacySemanticUiIcon';
import { CSSProperties, ReactNode } from 'react';

import styles from './Message.module.css';

type MessageColor = 'red' | 'orange' | 'yellow' | 'purple';

interface MessageProps {
  id?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  header?: ReactNode;
  content?: ReactNode;
  /** Renders each item as a bullet point */
  list?: ReactNode[];
  icon?: IconName;
  /** Error style. `error` is an alias of `negative` */
  negative?: boolean;
  error?: boolean;
  /** Success style. `success` is an alias of `positive` */
  positive?: boolean;
  success?: boolean;
  info?: boolean;
  warning?: boolean;
  color?: MessageColor;
}

const Message = ({
  id,
  className,
  style,
  children,
  header,
  content,
  list,
  icon,
  negative,
  error,
  positive,
  success,
  info,
  warning,
  color,
}: MessageProps) => {
  const hasShorthandContent = header || content || (list && list.length > 0);

  return (
    <div
      id={id}
      className={clsx(
        styles.message,
        (negative || error) && styles.negative,
        (positive || success) && styles.positive,
        info && styles.info,
        warning && styles.warning,
        color && styles[color],
        icon && styles.iconMessage,
        className,
      )}
      style={style}
    >
      {icon && <LegacySemanticUiIcon name={icon} className={styles.icon} />}
      {hasShorthandContent && (
        <MessageContent>
          {header && <MessageHeader>{header}</MessageHeader>}
          {content}
          {list && list.length > 0 && (
            <ul className={styles.list}>
              {list.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          )}
        </MessageContent>
      )}
      {children}
    </div>
  );
};

const MessageHeader = ({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) => <div className={clsx(styles.header, className)}>{children}</div>;

const MessageContent = ({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) => <div className={clsx(styles.content, className)}>{children}</div>;

Message.Header = MessageHeader;
Message.Content = MessageContent;

export default Message;
